/**
 * Shared authentication utilities
 * Provides common auth functions used across login flows (password, magic link, etc.)
 */

import { supabase } from '@/integrations/supabase/client';

import {
  evaluateMfaRequirement,
  hasPasskeyAuthentication,
  type MFARequirement,
} from '@backend-shared/auth/mfa-policy';

export type { MFARequirement } from '@backend-shared/auth/mfa-policy';

/**
 * Detect Supabase navigator.locks contention errors.
 *
 * Supabase auth-js uses navigator.locks to serialise token storage.
 * When multiple auth calls run concurrently (e.g. getUser + onAuthStateChange),
 * the SDK steals the lock from the slower call, which throws either:
 *   - AbortError (DOMException with name 'AbortError')
 *   - NavigatorLockAcquireTimeoutError (has isAcquireTimeout = true)
 *   - Error with message "released because another request stole it"
 *
 * These are transient — the onAuthStateChange listener delivers the
 * correct state independently. Safe to ignore in catch blocks.
 */
export function isLockContentionError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;
  if (e.name === 'AbortError') return true;
  if (e.isAcquireTimeout === true) return true;
  if (
    typeof e.message === 'string' &&
    e.message.includes('released because another request stole it')
  )
    return true;
  return false;
}

/**
 * Complete login process - checks membership and redirects based on role
 * Used by both password login and magic link authentication
 */
export async function completeLogin(userId: string): Promise<{
  shouldProceed: boolean;
  redirectPath: string;
  error?: string;
}> {
  try {
    // Fetch user role to determine if platform admin
    const { data: userRole } = await supabase
      .from('user_role_assignments')
      .select('role')
      .eq('user_id', userId)
      .single();

    const isPlatformAdmin = userRole?.role === 'admin';
    const isStaffOrAssistant = ['staff', 'assistant'].includes(userRole?.role || '');

    // If not a platform admin/staff/assistant, check if user has any active organization memberships
    if (!isPlatformAdmin && !isStaffOrAssistant) {
      const { data: activeMemberships, error: membershipError } = await supabase
        .from('organization_members')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .limit(1);

      if (membershipError) {
        console.error('Error checking memberships:', membershipError);
        // Continue with login - better to allow access than block due to query error
      } else if (!activeMemberships || activeMemberships.length === 0) {
        // User has no active organization memberships - block login.
        // Deliberately scope GLOBAL: a deprovisioned user's other devices
        // should not keep working either. The only other global caller is
        // the explicit sign-out-everywhere surface.
        await supabase.auth.signOut({ scope: 'global' });
        return {
          shouldProceed: false,
          redirectPath: '/login',
          error: 'Unable to access your account. Please contact your administrator for assistance.',
        };
      }
    }

    // Determine redirect path based on role
    const redirectPath = isPlatformAdmin || isStaffOrAssistant ? '/admin' : '/';

    return {
      shouldProceed: true,
      redirectPath,
    };
  } catch (error) {
    console.error('Complete login error:', error);
    return {
      shouldProceed: false,
      redirectPath: '/login',
      error: 'An unexpected error occurred.',
    };
  }
}

/**
 * Whether the current session's user holds the platform-admin role, for the
 * passkey step-up allowance. `null` means the answer could not be read — the
 * caller treats that the same as "admin" and demands the step-up, so an RLS
 * blip can only ever add a challenge, never waive one.
 *
 * Mirrors `requireAal2IfPlatformAdmin` in `_shared/auth/service-auth.ts`: same
 * table, same `role = 'admin'` predicate, and the own-row read is allowed at
 * aal1 by the "Users can view their own role" policy (verified there against
 * 20251107191134_fix_rls_performance.sql:563), so an aal1 passkey session can
 * always resolve its own answer.
 */
async function isPlatformAdminForStepUp(): Promise<boolean | null> {
  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (sessionError || !userId) {
      return null;
    }

    const { data, error } = await supabase
      .from('user_role_assignments')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .maybeSingle();

    if (error) {
      console.error('Error reading platform-admin role for step-up policy:', error.code);
      return null;
    }

    return data !== null;
  } catch (error) {
    if (!isLockContentionError(error)) {
      console.error('Error resolving step-up role policy:', error);
    }
    return null;
  }
}

/**
 * Whether a step-up is owed, with "could not be determined" as its own answer.
 *
 * `checkMFARequirement()` below collapses every failure to `needsMFA: false`,
 * which is fail-OPEN: a caller that gates app entry on it walks a TOTP-enrolled
 * user past their step-up whenever the AAL lookup blips. Callers that must fail
 * CLOSED read this instead and block on `indeterminate`.
 */
export async function checkMFARequirementStrict(): Promise<MFARequirement> {
  try {
    const { data: aal, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error || !aal) return { status: 'indeterminate' };
    const evidence = {
      currentLevel: aal.currentLevel,
      nextLevel: aal.nextLevel,
      authenticationMethods: aal.currentAuthenticationMethods,
      platformAdmin:
        aal.currentLevel === 'aal1' &&
        aal.nextLevel === 'aal2' &&
        hasPasskeyAuthentication(aal.currentAuthenticationMethods)
          ? await isPlatformAdminForStepUp()
          : null,
    };
    const preliminary = evaluateMfaRequirement(evidence);
    if (preliminary.status !== 'lookup_required') return preliminary;
    const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
    const result = evaluateMfaRequirement({
      ...evidence,
      factors: factorsError
        ? { available: false }
        : {
            available: true,
            verifiedFactorId:
              factors?.totp?.find((factor) => factor.status === 'verified')?.id ?? null,
          },
    });
    return result.status === 'lookup_required' ? { status: 'indeterminate' } : result;
  } catch {
    return { status: 'indeterminate' };
  }
}

/**
 * Check if user has MFA enabled and needs verification
 *
 * Lossy view of `checkMFARequirementStrict()`: `indeterminate` reports as
 * `needsMFA: false`, preserving the fail-open behaviour every existing caller
 * was written against.
 */
export async function checkMFARequirement(): Promise<{
  needsMFA: boolean;
  factorId: string | null;
}> {
  const requirement = await checkMFARequirementStrict();

  return requirement.status === 'required'
    ? { needsMFA: true, factorId: requirement.factorId }
    : { needsMFA: false, factorId: null };
}

/**
 * Verify MFA code
 */
export async function verifyMFACode(
  factorId: string,
  code: string,
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    if (!factorId || code.length !== 6) {
      return {
        success: false,
        error: 'Invalid code format',
      };
    }

    // Create MFA challenge
    const challengeData = await supabase.auth.mfa.challenge({
      factorId,
    });

    if (challengeData.error) {
      return {
        success: false,
        error: challengeData.error.message,
      };
    }

    // Verify the code
    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.data.id,
      code,
    });

    if (error) {
      return {
        success: false,
        error: error.message,
      };
    }

    return {
      success: true,
    };
  } catch (error: unknown) {
    console.error('MFA verification error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Invalid verification code',
    };
  }
}
